import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import DpinModal from './DpinModal';

const Dashboard = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [showDpinModal, setShowDpinModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const name = Cookies.get('userName');
    const role = Cookies.get('userRole');
    if (name && role) {
      setUserName(name);
      setUserRole(role);
    }

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleManualLogout = () => setShowDpinModal(true);

  const handleLogoutConfirmed = () => {
    Cookies.remove('token');
    Cookies.remove('userName');
    Cookies.remove('userRole');
    navigate('/login', { replace: true });
  };

  const initials = userName
    ? userName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  const greeting = () => {
    const h = currentTime.getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatDate = (d) =>
    d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const formatTime = (d) =>
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const stats = [
    { label: 'Session', value: 'Live', accent: '#22c55e', dot: true },
    { label: 'Role', value: userRole || 'User', accent: '#60a5fa' },
    { label: 'Access', value: 'Standard', accent: '#f59e0b' },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@300;400;500&family=Outfit:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .db-root {
          min-height: 100vh;
          background: #0c0d0f;
          // background: #031334;
          font-family: 'Outfit', sans-serif;
          color: #e8e9ec;
          display: flex;
          flex-direction: column;
        }

        /* ── Topbar ── */
        .db-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 32px;
          border-bottom: 1px solid #1f2025;
          background: #0e0f12;
        }

        .db-topbar-left {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .db-topbar-date {
          font-size: 10px;
          font-family: 'DM Mono', monospace;
          color: #4b5563;
          letter-spacing: 0.8px;
          text-transform: uppercase;
        }
        .db-topbar-time {
          font-size: 18px;
          font-family: 'DM Mono', monospace;
          color: #e8e9ec;
          font-weight: 500;
          letter-spacing: 1px;
        }

        .db-topbar-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .db-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          background: #111215;
          border: 1px solid #1f2025;
          border-radius: 20px;
          font-size: 11px;
          color: #6b7280;
          font-family: 'DM Mono', monospace;
        }
        .db-badge-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #22c55e;
          box-shadow: 0 0 6px #22c55e;
          animation: pulse 2s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .db-avatar {
          width: 34px; height: 34px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1d4ed8, #6366f1);
          display: flex; align-items: center; justify-content: center;
          font-size: 12px;
          font-weight: 600;
          color: #fff;
          letter-spacing: 0.5px;
          border: 2px solid #2a2d35;
          flex-shrink: 0;
        }

        /* ── Page body ── */
        .db-body {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 24px;
        }

        .db-card {
          width: 100%;
          max-width: 560px;
          display: flex;
          flex-direction: column;
          gap: 28px;
        }

        /* ── Hero ── */
        .db-hero {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .db-greeting {
          font-size: 10.5px;
          font-family: 'DM Mono', monospace;
          color: #4b5563;
          text-transform: uppercase;
          letter-spacing: 1.5px;
        }
        .db-hero-name {
          font-family: 'DM Serif Display', serif;
          font-size: 40px;
          color: #f1f2f4;
          line-height: 1.1;
          letter-spacing: -0.5px;
        }
        .db-hero-name em {
          font-style: italic;
          color: #60a5fa;
        }
        .db-hero-sub {
          font-size: 13.5px;
          color: #4b5563;
          font-weight: 300;
          margin-top: 2px;
        }

        /* ── Divider ── */
        .db-divider {
          width: 100%;
          height: 1px;
          background: #1f2025;
        }

        /* ── Stat cards ── */
        .db-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        .db-stat-card {
          background: #111215;
          border: 1px solid #1f2025;
          border-radius: 10px;
          padding: 16px 18px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: relative;
          overflow: hidden;
        }
        .db-stat-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: var(--accent);
          opacity: 0.65;
        }
        .db-stat-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #4b5563;
          font-family: 'DM Mono', monospace;
        }
        .db-stat-value {
          font-size: 20px;
          font-weight: 600;
          color: #e8e9ec;
          display: flex;
          align-items: center;
          gap: 7px;
        }
        .db-stat-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: var(--accent);
          box-shadow: 0 0 7px var(--accent);
          animation: pulse 2s ease-in-out infinite;
          flex-shrink: 0;
        }

        /* ── Info panel ── */
        .db-panel {
          background: #111215;
          border: 1px solid #1f2025;
          border-radius: 10px;
          padding: 18px 22px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
        }
        .db-panel-text p {
          font-size: 12px;
          color: #4b5563;
          line-height: 1.6;
          font-weight: 300;
        }
        .db-panel-chip {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          background: #1a1c20;
          border: 1px solid #2a2d35;
          border-radius: 7px;
          font-size: 11.5px;
          color: #6b7280;
          white-space: nowrap;
          font-family: 'DM Mono', monospace;
          flex-shrink: 0;
        }

        /* ── Logout button ── */
        .db-logout-btn {
          width: 100%;
          padding: 12px;
          background: transparent;
          border: 1px solid #2a2d35;
          border-radius: 9px;
          color: #6b7280;
          font-size: 13px;
          font-family: 'Outfit', sans-serif;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          letter-spacing: 0.2px;
        }
        .db-logout-btn:hover {
          background: #1f1a1a;
          border-color: #ef4444;
          color: #ef4444;
        }

        @media (max-width: 480px) {
          .db-stats { grid-template-columns: 1fr; }
          .db-hero-name { font-size: 28px; }
          .db-topbar { padding: 14px 20px; }
          .db-badge { display: none; }
        }
      `}</style>

      <div className="db-root">
        {/* Topbar */}
        <header className="db-topbar">
          <div className="db-topbar-left">
            <span className="db-topbar-date">{formatDate(currentTime)}</span>
            <span className="db-topbar-time">{formatTime(currentTime)}</span>
          </div>
          <div className="db-topbar-right">
            <div className="db-badge">
              <div className="db-badge-dot" />
              Authenticated
            </div>
            <div className="db-avatar">{initials}</div>
          </div>
        </header>

        {/* Centered content */}
        <div className="db-body">
          <div className="db-card">
            {/* Hero */}
            <div className="db-hero">
              <span className="db-greeting">{greeting()}</span>
              <h1 className="db-hero-name">
                Hello, <em>{userName || 'User'}</em>
              </h1>
              <p className="db-hero-sub">
                You're successfully logged in and your session is active.
              </p>
            </div>

            <div className="db-divider" />

            {/* Stats */}
            <div className="db-stats">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="db-stat-card"
                  style={{ '--accent': s.accent }}
                >
                  <span className="db-stat-label">{s.label}</span>
                  <span className="db-stat-value">
                    {s.dot && <span className="db-stat-dot" />}
                    {s.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Info panel */}
            <div className="db-panel">
              <div className="db-panel-text">
                <p>
                  Your session is protected.
                </p>
              </div>
              <div className="db-panel-chip">🔐 DPIN protected</div>
            </div>

          </div>
        </div>
      </div>

      <DpinModal
        isOpen={showDpinModal}
        onClose={() => setShowDpinModal(false)}
        onSuccess={handleLogoutConfirmed}
        itemType="session"
      />
    </>
  );
};

export default Dashboard;